import { useEffect, useMemo, useState } from "react";
import { Box, Flex, Select, SimpleGrid, Spinner, Text } from "@chakra-ui/react";
import { crm, Pill } from "../../theme";
import { getCompanies } from "../../services/companyService";
import { getClientBranches } from "../../services/commercialClientService";
import { getBillingSetting, getBillingSettings } from "../../services/billingSettingService";
import { CommercialClientBranch } from "../../models/CommercialClient";
import { BillingSetting, BillingSettingKind } from "../../models/BillingSetting";
import { EntitySearch, Option } from "./SaveQuoteModal";
import { toneColors } from "../process/components/duimp";

export interface ClientSelection {
  companyId: string | null;
  companyName: string;
  branch: CommercialClientBranch | null;
  /** Parametrização escolhida (com exceções), por modo do simulador. */
  settings: Partial<Record<BillingSettingKind, BillingSetting | null>>;
}

export const emptySelection: ClientSelection = { companyId: null, companyName: "", branch: null, settings: {} };

const formatCnpj = (d: string) =>
  d.length === 14 ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}` : d;

const UNIT_SHORT: Record<number, string> = { 1: "Logística", 2: "Distribuidora", 3: "Rondônia", 4: "W Trading" };
/** Nome + unidade emissora (há nomes repetidos entre unidades no Gett). */
export const settingLabel = (s: BillingSetting) =>
  s.unitType ? `${s.name} · ${UNIT_SHORT[s.unitType] ?? ""}${s.unitState ? ` ${s.unitState}` : ""}` : s.name;

const percent = (value?: number | null) =>
  value == null ? "—" : `${value.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}%`;

/**
 * Cliente cadastrado + filial (Comercial › Clientes Ativos) + parametrização fiscal (modelo Gett) no topo do simulador.
 * A filial (UF) e o cliente escolhem a exceção da parametrização; as do cliente aparecem primeiro.
 */
const ClientBranchPicker = ({
  value,
  onChange,
  kind,
  emptyLabel = "Sem parametrização (alíquotas à mão)",
  onUserChange,
  autoPick = true,
}: {
  value: ClientSelection;
  /** Atualização funcional: filial e parametrização chegam assíncronas e não podem se sobrescrever. */
  onChange: (update: (prev: ClientSelection) => ClientSelection) => void;
  /** Tipo de parametrização do modo atual. */
  kind: BillingSettingKind;
  /** Opção vazia da parametrização (o simulador aceita alíquotas à mão; a venda não). */
  emptyLabel?: string;
  /** Só escolhas da pessoa (as automáticas — filial única, parametrização única — não chamam). */
  onUserChange?: (what: "company" | "branch" | "setting") => void;
  /** Escolhe sozinho a parametrização única do cliente (desligado ao editar algo salvo: não troca o que foi gravado). */
  autoPick?: boolean;
}) => {
  const [companies, setCompanies] = useState<Option[]>([]);
  const [branches, setBranches] = useState<CommercialClientBranch[]>([]);
  const [settings, setSettings] = useState<BillingSetting[]>([]);
  const [loadingSetting, setLoadingSetting] = useState(false);

  useEffect(() => {
    getCompanies(true).then((list) =>
      setCompanies(
        (Array.isArray(list) ? list : [])
          .filter((c: any) => c.id)
          .map((c: any) => ({ id: c.id, label: c.name, sub: c.cnpj })),
      ),
    );
    getBillingSettings()
      .then(setSettings)
      .catch(() => setSettings([]));
  }, []);

  useEffect(() => {
    if (!value.companyId) {
      setBranches([]);
      return;
    }
    let active = true;
    getClientBranches(value.companyId)
      .then((list) => {
        if (!active) return;
        setBranches(list);
        // Uma filial só: já seleciona
        if (autoPick && list.length === 1) onChange((prev) => (prev.branch ? prev : { ...prev, branch: list[0] }));
      })
      .catch(() => active && setBranches([]));
    return () => {
      active = false;
    };
  }, [value.companyId]);

  const ofKind = useMemo(() => settings.filter((s) => s.kind === kind), [settings, kind]);
  const linked = ofKind.filter((s) => value.companyId && s.companyId === value.companyId);
  const others = ofKind.filter((s) => !(value.companyId && s.companyId === value.companyId));
  const current = value.settings[kind] ?? null;

  const setSetting = (companyId: string | null, setting: BillingSetting | null) =>
    // Ignora resposta atrasada de outro cliente
    onChange((prev) => (prev.companyId !== companyId ? prev : { ...prev, settings: { ...prev.settings, [kind]: setting } }));

  const pickSetting = async (id: string) => {
    const companyId = value.companyId;
    if (!id) return setSetting(companyId, null);
    setLoadingSetting(true);
    try {
      setSetting(companyId, await getBillingSetting(id));
    } catch {
      setSetting(companyId, null);
    } finally {
      setLoadingSetting(false);
    }
  };

  // Cliente com uma parametrização do tipo ligada: já escolhe
  useEffect(() => {
    if (!autoPick || !value.companyId || current) return;
    const mine = settings.filter((s) => s.kind === kind && s.companyId === value.companyId);
    if (mine.length === 1) pickSetting(mine[0].id!);
  }, [value.companyId, kind, settings]);

  const branch = value.branch;

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing="12px" alignItems="end">
        <EntitySearch
          label="Cliente cadastrado"
          placeholder="Buscar empresa por nome ou CNPJ"
          options={companies}
          value={value.companyId}
          onChange={(o) => { onUserChange?.("company"); onChange(() => ({ companyId: o?.id ?? null, companyName: o?.label ?? "", branch: null, settings: {} })); }}
        />
        <Box>
          <Text fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">
            Filial (UF)
          </Text>
          <Select
            size="sm"
            bg="white"
            value={branch?.id ?? ""}
            isDisabled={!value.companyId || branches.length === 0}
            placeholder={
              !value.companyId ? "Escolha o cliente" : branches.length === 0 ? "Sem filiais no Clientes Ativos" : "Escolha a filial"
            }
            onChange={(e) => {
              const next = branches.find((b) => b.id === e.target.value) ?? null;
              onUserChange?.("branch");
              onChange((prev) => ({ ...prev, branch: next }));
            }}
          >
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {(b.name ? `${b.name} · ` : "") + `${formatCnpj(b.cnpj)} · ${b.state}`}
              </option>
            ))}
          </Select>
        </Box>
        <Box>
          <Flex align="center" gap="6px" mb="4px">
            <Text fontSize="12px" fontWeight="600" color={crm.textMuted}>
              Parametrização fiscal
            </Text>
            {loadingSetting && <Spinner size="xs" />}
          </Flex>
          <Select size="sm" bg="white" value={current?.id ?? ""} onChange={(e) => { onUserChange?.("setting"); pickSetting(e.target.value); }}>
            <option value="">{ofKind.length ? emptyLabel : "Nenhuma cadastrada (Configurações)"}</option>
            {linked.length > 0 && (
              <optgroup label="Do cliente">
                {linked.map((s) => <option key={s.id} value={s.id}>{settingLabel(s)}</option>)}
              </optgroup>
            )}
            {others.length > 0 && (
              <optgroup label={linked.length ? "Outras" : "Parametrizações"}>
                {others.map((s) => <option key={s.id} value={s.id}>{settingLabel(s)}</option>)}
              </optgroup>
            )}
          </Select>
        </Box>
      </SimpleGrid>
      {current && (
        <Flex gap="6px" wrap="wrap" mt="10px" align="center">
          {current.rates.cfop && <Pill {...toneColors.neutral}>CFOP {current.rates.cfop}</Pill>}
          <Pill {...toneColors.neutral}>ICMS {percent(current.rates.icmsPercent)}</Pill>
          {(current.rates.icmsBaseReductionPercent ?? 0) > 0 && <Pill {...toneColors.neutral}>Red. BC {percent(current.rates.icmsBaseReductionPercent)}</Pill>}
          {(current.rates.presumedCreditPercent ?? 0) > 0 && (
            <Pill {...toneColors.neutral}>Créd. presumido {percent(current.rates.presumedCreditPercent)}</Pill>
          )}
          {kind === "encomenda" && <Pill {...toneColors.neutral}>PIS/COFINS {percent(current.rates.pisPercent)} / {percent(current.rates.cofinsPercent)}</Pill>}
          {((current.rates.ibsStatePercent ?? 0) + (current.rates.ibsCityPercent ?? 0) + (current.rates.cbsPercent ?? 0)) > 0 && (
            <Pill {...toneColors.neutral}>
              IBS {percent((current.rates.ibsStatePercent ?? 0) + (current.rates.ibsCityPercent ?? 0))} · CBS {percent(current.rates.cbsPercent)}
            </Pill>
          )}
          {(current.exceptions?.length ?? 0) > 0 && (
            <Pill {...toneColors.success}>{current.exceptions!.length} exceções (NCM/UF/cliente)</Pill>
          )}
          {!branch && (current.exceptions ?? []).some((e) => e.state) && (
            <Text fontSize="12px" color={crm.warning}>Escolha a filial: há exceções por UF.</Text>
          )}
        </Flex>
      )}
    </Box>
  );
};

export default ClientBranchPicker;
